// iter150AZ.28 — Service-history popover for a single compressor SN.
//
// Triggered from the SN chip on a quote line item. Loads
// ``GET /api/compressors/{serial_id}/service-history`` which returns
// ALL service events bound to this compressor (every Quote_Detail
// row whose `Related to Product SN` matches), grouped by parent
// quote/invoice newest-first.
//
// Rendered as an absolutely-positioned card hanging below the chip.
// On click outside or Escape, closes itself.
import React, { useEffect, useRef, useState } from "react";
import { X as XIcon, Spinner, Receipt, FileText, WarningCircle, ArrowSquareOut, FilePdf } from "@phosphor-icons/react";
import api from "../lib/api";
import { formatMoney } from "../lib/cart";
import { useT } from "../lib/i18n";
import { useCompressorServiceHistory } from "../lib/queryHooks";

function _fmtDate(iso) {
  if (!iso) return "—";
  const s = String(iso).slice(0, 10);
  return s || "—";
}

export default function ServiceHistoryPopover({
  serialId, serialNumber, currency, onClose, anchorRect,
}) {
  const t = useT();
  // iter150AZ.33 — Cached via react-query: a repeat click on the same
  // SN chip within 30s reuses the previous response, no extra GET.
  const { data, isLoading: loading, error: queryError } =
    useCompressorServiceHistory(serialId);
  const [error, setError] = useState("");
  useEffect(() => {
    setError(queryError ? (queryError?.response?.data?.detail
                            || "Could not load service history") : "");
  }, [queryError]);
  const [expanded, setExpanded] = useState({});   // {quote_id: bool}
  // iter150AZ.29 — PDF export state. ``downloading`` toggles a spinner
  // on the button so reps know the request is in flight (the report
  // can take a couple seconds on compressors with long histories).
  const [downloading, setDownloading] = useState(false);
  const containerRef = useRef(null);

  const downloadPdf = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const r = await api.get(
        `/compressors/${encodeURIComponent(serialId)}/service-history/pdf`,
        { params: { currency: currency || "USD" }, responseType: "blob" },
      );
      // Trigger a download via a transient <a download>.
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ServiceHistory_${serialNumber || serialId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Revoke after a beat — Safari needs the URL alive briefly.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not generate PDF");
    } finally {
      setDownloading(false);
    }
  };

  // Click-outside-to-close + Escape-to-close handlers.
  useEffect(() => {
    const onClick = (e) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target)) onClose && onClose();
    };
    const onKey = (e) => { if (e.key === "Escape") onClose && onClose(); };
    // Defer so the click that opened the popover doesn't immediately close it.
    const handle = setTimeout(() => {
      document.addEventListener("mousedown", onClick);
      document.addEventListener("keydown", onKey);
    }, 0);
    return () => {
      clearTimeout(handle);
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  // Position the popover near the anchor. Falls back to centered if no rect.
  const style = anchorRect ? (() => {
    const top = Math.min(anchorRect.bottom + 6 + window.scrollY,
                          window.innerHeight + window.scrollY - 80);
    const left = Math.max(8,
                  Math.min(anchorRect.left + window.scrollX,
                           window.innerWidth - 460));
    return { position: "absolute", top, left, width: 440, maxHeight: "70vh", zIndex: 60 };
  })() : { position: "fixed", top: "20vh", left: "50%", transform: "translateX(-50%)",
           width: 440, maxHeight: "70vh", zIndex: 60 };

  return (
    <div ref={containerRef}
         data-testid="service-history-popover"
         className="bg-white border border-slate-200 rounded-sm shadow-2xl flex flex-col overflow-hidden"
         style={style}>
      {/* Header */}
      <div className="px-3 py-2 border-b border-slate-200 bg-slate-50/60 flex items-center justify-between">
        <div className="min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
            {t("svc_hist.title", "Service history")}
          </div>
          <div className="text-[12px] font-mono font-bold text-slate-900 truncate">
            SN {serialNumber || serialId}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {/* iter150AZ.29 — Print/Export PDF. Disabled while initial
              load is in flight; spinner replaces the icon during the
              actual PDF round-trip. Hidden when the event list is
              empty (no point exporting an empty timeline). */}
          {data && data.event_count > 0 && (
            <button type="button"
                    onClick={downloadPdf}
                    disabled={downloading}
                    data-testid="service-history-print"
                    title={t("svc_hist.print_title",
                             "Export the full service history as a PDF")}
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 h-7
                               rounded-sm border border-slate-300 text-slate-700 hover:bg-slate-100
                               disabled:opacity-60">
              {downloading
                ? <Spinner size={11} weight="bold" className="animate-spin" />
                : <FilePdf size={11} weight="bold" />}
              {t("svc_hist.print", "PDF")}
            </button>
          )}
          <button type="button"
                  onClick={onClose}
                  data-testid="service-history-close"
                  className="text-slate-400 hover:text-slate-700 ml-1">
            <XIcon size={14} weight="bold" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        {loading && (
          <div className="px-3 py-4 text-[12px] text-slate-500 inline-flex items-center gap-2">
            <Spinner size={12} weight="bold" className="animate-spin" />
            {t("svc_hist.loading", "Loading service history…")}
          </div>
        )}

        {!loading && error && (
          <div className="m-3 px-3 py-2 bg-rose-50 border border-rose-200 text-rose-800 text-[11px] rounded-sm flex items-start gap-2"
               data-testid="service-history-error">
            <WarningCircle size={13} weight="bold" className="flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {!loading && !error && data && (
          <>
            {/* Summary banner */}
            <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-100"
                 data-testid="service-history-summary">
              {data.event_count === 0 ? (
                <span className="text-slate-400 italic normal-case tracking-normal">
                  {t("svc_hist.empty",
                     "No service events on file for this compressor yet.")}
                </span>
              ) : (
                <>
                  <span className="font-bold text-slate-700">{data.event_count}</span>
                  &nbsp;{t("svc_hist.events", "events")}
                  &nbsp;·&nbsp;
                  <span className="font-bold text-slate-700">{data.invoice_count}</span>
                  &nbsp;{t("svc_hist.invoiced", "invoiced")}
                  &nbsp;·&nbsp;
                  <span className="font-bold text-slate-700">{data.count}</span>
                  &nbsp;{t("svc_hist.lines", "lines")}
                </>
              )}
            </div>

            {/* Event list (newest first) */}
            <ul className="divide-y divide-slate-100">
              {(data.items || []).map((ev) => {
                const isExpanded = !!expanded[ev.quote_id];
                const icon = ev.is_invoice ? Receipt : FileText;
                const Icon = icon;
                const accent =
                  ev.is_invoice ? "text-emerald-700"
                  : (ev.q_type === "Warranty") ? "text-amber-700"
                  : "text-slate-600";
                return (
                  <li key={ev.quote_id}
                      data-testid={`service-history-event-${ev.quote_id}`}
                      className="px-3 py-2">
                    <button type="button"
                            onClick={() => setExpanded(p => ({ ...p, [ev.quote_id]: !p[ev.quote_id] }))}
                            data-testid={`service-history-toggle-${ev.quote_id}`}
                            className="w-full text-left">
                      <div className="flex items-start gap-2">
                        <Icon size={13} weight="bold" className={`flex-shrink-0 mt-0.5 ${accent}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center flex-wrap gap-x-2 text-[12px]">
                            <span className="font-mono font-bold text-slate-900 truncate">
                              {ev.is_invoice
                                ? <>INV #{ev.invoice_number}</>
                                : <>Q #{ev.quote_id}</>}
                            </span>
                            <span className="text-slate-500">{_fmtDate(ev.date)}</span>
                            {ev.q_type && ev.q_type !== "Sales" && (
                              <span className={`text-[9px] uppercase tracking-wider font-bold px-1 py-0.5 rounded-sm ${
                                ev.q_type === "Warranty" ? "bg-amber-100 text-amber-800"
                                : ev.q_type === "Return" ? "bg-rose-100 text-rose-800"
                                : "bg-slate-100 text-slate-700"
                              }`}>
                                {ev.q_type}
                              </span>
                            )}
                          </div>
                          <div className="text-[10px] text-slate-500 mt-0.5">
                            {ev.lines.length} {t("svc_hist.lines_short", "line")}{ev.lines.length === 1 ? "" : "s"}
                            {Number(ev.subtotal) > 0 && (
                              <span className="ml-2">· {formatMoney(ev.subtotal, currency)}</span>
                            )}
                            {ev.prepared_by && <span className="ml-2 truncate">· {ev.prepared_by}</span>}
                          </div>
                        </div>
                        <a href={`/quotes/${encodeURIComponent(ev.quote_id)}`}
                           target="_blank" rel="noopener noreferrer"
                           data-testid={`service-history-open-${ev.quote_id}`}
                           onClick={(e) => e.stopPropagation()}
                           className="text-slate-400 hover:text-blue-700 flex-shrink-0"
                           title={t("svc_hist.open_quote", "Open quote in new tab")}>
                          <ArrowSquareOut size={12} weight="bold" />
                        </a>
                      </div>
                    </button>

                    {/* Expanded line list */}
                    {isExpanded && (
                      <ul className="mt-2 ml-5 border-l border-slate-200 pl-3 space-y-1"
                          data-testid={`service-history-lines-${ev.quote_id}`}>
                        {ev.lines.map((ln) => (
                          <li key={ln.detail_id}
                              className="text-[11px] text-slate-700 flex items-baseline justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <span className="font-semibold text-slate-900 truncate">
                                {ln.product_name || ln.product_code}
                              </span>
                              <span className="ml-2 text-[10px] text-slate-400 font-mono">
                                ×{ln.quantity}
                              </span>
                              {ln.explanation && (
                                <div className="text-[10px] italic text-slate-500 truncate">
                                  {ln.explanation}
                                </div>
                              )}
                            </div>
                            <span className="font-mono text-[10px] text-slate-600 flex-shrink-0">
                              {formatMoney(ln.line_total, currency)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>

      {/* Footer */}
      {!loading && data && data.event_count > 0 && (
        <div className="px-3 py-2 border-t border-slate-200 bg-slate-50/60 text-[10px] text-slate-500 italic">
          {t("svc_hist.footer",
             "Newest first · click a row to expand line items · click ↗ to open the quote.")}
        </div>
      )}
    </div>
  );
}
